import React, { useCallback, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import {
  equals,
  find,
  lensPath,
  map,
  mergeRight,
  omit,
  prop,
  propEq,
  set,
  toPairs,
} from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import YesNoCard from "../../components/YesNoCard";
import EditIcon from "@mui/icons-material/Edit";
import { Fields, getFieldNameDisplay } from "../FieldOptions";
import ProjectFieldOptionDialog from "../edit/ProjectFieldOptionDialog";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 75,
  },
}));

const ProjectFieldOptionsCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [selectedFieldOption, setSelectedFieldOption] = useState({});
  const [selectedFieldName, setSelectedFieldName] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAddClick = useCallback(() => {
    setSelectedFieldOption(null);
    setSelectedFieldName(null);
    setDialogOpen(true);
  }, []);

  const handleEditClick = useCallback((fieldName, fieldOption) => {
    setSelectedFieldOption(fieldOption);
    setSelectedFieldName(fieldName);
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedFieldOption({});
    setSelectedFieldName(null);
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async (name, data) => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      setSelectedFieldOption({});
      setSelectedFieldName(null);

      console.log(data);
      const update = set(lensPath(["fieldOptions", name]), data, project);

      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async fieldLabel => {
      console.log("Deleting Field Option:");
      console.log(fieldLabel);
      const update = mergeRight(project, {
        fieldOptions: omit([fieldLabel], project.fieldOptions),
      });

      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Field Options"
        action={
          <Tooltip title="Add Field Option">
            <IconButton onClick={handleAddClick} size="large">
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project.fieldOptions) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Field Options set up for this Project.
          </Box>
        )}
        {notBlank(project.fieldOptions) && (
          <TableContainer style={{ maxHeight: 350, overflowY: "auto" }}>
            <Table stickyHeader aria-label="Field Options">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="field-name"
                    align="left"
                    className={classes.cell}
                  >
                    Field Name
                  </TableCell>

                  <TableCell
                    key="field-label"
                    align="left"
                    className={classes.cell}
                  >
                    Field Label
                  </TableCell>

                  <TableCell
                    key="hidden"
                    align="center"
                    className={classes.cell}
                  >
                    Hidden
                  </TableCell>

                  <TableCell
                    key="required"
                    align="center"
                    className={classes.cell}
                  >
                    Required
                  </TableCell>

                  <TableCell
                    key="default-value"
                    align="left"
                    className={classes.cell}
                  >
                    Default Value
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 75 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(
                  ([fieldName, options]) => (
                    <TableRow hover tabIndex={-1} key={fieldName}>
                      <TableCell
                        key={"field-name:" + options.projectFieldOptionId}
                        align="left"
                        className={classes.cell}
                      >
                        {getFieldNameDisplay(fieldName)}
                      </TableCell>

                      <TableCell
                        key={"field-label:" + options.projectFieldOptionId}
                        align="left"
                        className={classes.cell}
                      >
                        {options.fieldLabel}
                      </TableCell>

                      <TableCell
                        key={"hidden:" + options.projectFieldOptionId}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard value={options.hidden} showNoIcon={false} />
                      </TableCell>

                      <TableCell
                        key={"required:" + options.projectFieldOptionId}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard
                          value={options.required}
                          showNoIcon={false}
                        />
                      </TableCell>

                      <TableCell
                        key={"default-value:" + options.projectFieldOptionId}
                        align="left"
                        className={classes.cell}
                      >
                        {equals(fieldName, Fields.sub_projects.value)
                          ? prop(
                              "subProjectName",
                              find(
                                propEq("subProjectId", options.defaultValue),
                                project.subProjects,
                              ),
                            )
                          : options.defaultValue}
                      </TableCell>

                      <TableCell
                        key={"actions:" + options.projectFieldOptionId}
                        align="center"
                        style={{ width: 75 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          pl={4}
                        >
                          <Tooltip title="Edit">
                            <IconButton
                              classes={{ label: classes.icon }}
                              onClick={() =>
                                handleEditClick(fieldName, options)
                              }
                              size="large"
                            >
                              <EditIcon color="primary" fontSize="large" />
                            </IconButton>
                          </Tooltip>
                          <ConfirmButton
                            handleDelete={() => handleDeleteClick(fieldName)}
                            dialogTitle="Remove Field Option"
                            dialogBody="Are you sure you want to remove this field option?"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ),
                  toPairs(project.fieldOptions),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      {dialogOpen && (
        <ProjectFieldOptionDialog
          open={dialogOpen}
          name={selectedFieldName}
          fieldOption={selectedFieldOption}
          project={project}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default ProjectFieldOptionsCard;
